import { Box } from "@mui/material";
import { useCallback, useState } from "react";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../api/adminApi";
import type { Approval } from "../../api/types/approvalTypes";
import EntitySection from "../entityFields/EntitySection";
import EntitySectionHeader from "../entityFields/EntitySectionHeader";
import ApprovalDetailsDrawer from "./approval-details/ApprovalDetailsDrawer";
import ApprovalActionDialog from "./ApprovalActionDialog";
import ApprovalActions from "./ApprovalActions";
import ApprovalInfoTable from "./ApprovalInfoTable";
import ReassignApproversDialog from "./ReassignApproversDialog";

export interface RequestOrigin {
  requestedByUserName: string;
  requestedAt: string;
}

interface Props {
  approvalId: string;
  requestOrigin?: RequestOrigin;
  showActionChecks?: boolean;
  onApprovalActionExecuted: () => void;
  hideSecondaryActions?: boolean;
}

interface DialogState {
  openDialog?: "approve" | "reject" | "cancel" | "reassign";
}

const ApprovalWidget = ({
  approvalId,
  requestOrigin,
  showActionChecks,
  onApprovalActionExecuted,
  hideSecondaryActions,
}: Props) => {
  const [dialogState, setDialogState] = useState<DialogState>({});
  const [showDetails, setShowDetails] = useState(false);

  const getApproval = useCallback(() => adminApi.getApproval(approvalId), [approvalId]);

  const [approval, fetchError, { setData: setApproval }] = useFetch(getApproval);

  if (fetchError) {
    logError(fetchError, "[ApprovalWidget] getApproval");
    return null;
  }

  if (approval === undefined) {
    return null;
  }

  if (approval.status === "Cancelled") {
    return null;
  }

  const handleApplyAction = (updatedApproval: Approval) => {
    setApproval(updatedApproval);
    onApprovalActionExecuted();
  };

  return (
    <>
      <EntitySection>
        <EntitySectionHeader title="Approval">
          <ApprovalActions
            approval={approval}
            onActionRequest={(action) => setDialogState({ openDialog: action })}
            hideSecondaryActions={hideSecondaryActions}
          />
        </EntitySectionHeader>

        <Box sx={{ px: 1.5, pb: 1.5 }}>
          <ApprovalInfoTable
            approval={approval}
            requestOrigin={requestOrigin}
            onShowDetails={() => setShowDetails(true)}
          />
        </Box>
      </EntitySection>
      {dialogState.openDialog && dialogState.openDialog !== "reassign" && (
        <ApprovalActionDialog
          action={dialogState.openDialog}
          approval={approval}
          showActionChecks={showActionChecks}
          onClose={() => setDialogState({})}
          onApply={handleApplyAction}
        />
      )}
      {dialogState.openDialog && dialogState.openDialog === "reassign" && (
        <ReassignApproversDialog approval={approval} onClose={() => setDialogState({})} onApply={handleApplyAction} />
      )}
      {showDetails && (
        <ApprovalDetailsDrawer
          approval={approval}
          requestOrigin={requestOrigin}
          showActionChecks={showActionChecks}
          hideSecondaryActions={hideSecondaryActions}
          onClose={() => setShowDetails(false)}
          onApplyAction={handleApplyAction}
        />
      )}
    </>
  );
};

export default ApprovalWidget;
